import { useEffect, useMemo, useState } from "react";
import Autocomplete from "@/components/Autocomplete";
import Icon from "@/components/Icon";
import type { Section } from "@/types";

type Props = {
  sections: Section[];
  sectionId: string;
  onSectionChange: (sectionId: string) => void;
};

export default function SectionRosterSectionPicker({ sections, sectionId, onSectionChange }: Props) {
  const selectedSection = useMemo(
    () => sections.find((section) => section.id === sectionId) ?? null,
    [sections, sectionId]
  );
  const [query, setQuery] = useState(selectedSection?.name ?? "");

  useEffect(() => {
    setQuery(selectedSection?.name ?? "");
  }, [selectedSection?.name]);

  if (sections.length <= 1) {
    return null;
  }

  const filteredSections = sections.filter((section) =>
    section.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="max-w-sm" data-testid="section-roster-section-picker">
      <Autocomplete
        variant="searchableDropdown"
        label="Section"
        query={query}
        data={filteredSections}
        menuClassName="max-h-[20rem]"
        onChangeText={(value) => {
          setQuery(value);
        }}
        onSelect={(section) => {
          onSectionChange(section.id);
          setQuery(section.name);
        }}
        reset={() => {
          if (selectedSection) {
            setQuery(selectedSection.name);
          }
        }}
        renderItem={(section) => (
          <div className="flex items-center border-bottom">
            <div className="bg-sys-brand-secondary-container rounded-full p-2 mr-4 flex items-center justify-center">
              <Icon name="domain" size={24} />
            </div>
            <div className="text-body-large">{section.name}</div>
          </div>
        )}
      />
    </div>
  );
}
